import { useEffect, useRef, useCallback } from 'react';
import { heartbeat } from '@utils/api';

// How often to send heartbeat when active (5 minutes)
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

// How long after last activity before we consider user inactive (2 minutes)
const ACTIVITY_TIMEOUT_MS = 2 * 60 * 1000;

/**
 * Hook that sends periodic heartbeat requests to keep the session alive
 * when the user is active. Detects activity via mouse, keyboard, and touch events.
 */
export function useSessionHeartbeat(enabled: boolean) {
  const lastActivityRef = useRef<number>(Date.now());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updateLastActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const sendHeartbeat = useCallback(async () => {
    const timeSinceActivity = Date.now() - lastActivityRef.current;

    // Only send heartbeat if user was active recently
    if (timeSinceActivity < ACTIVITY_TIMEOUT_MS) {
      try {
        await heartbeat();
      } catch (error) {
        // Heartbeat failures are non-critical - session will expire naturally
        // 401 errors are handled by the SWR error handler which redirects to login
        console.debug('Heartbeat failed:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Track user activity
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((event) => {
      window.addEventListener(event, updateLastActivity, { passive: true });
    });

    // Send initial heartbeat
    sendHeartbeat();

    // Set up periodic heartbeat
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, updateLastActivity);
      });
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, updateLastActivity, sendHeartbeat]);
}
